// src/screens/GestionAsambleasScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  doc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';
import { 
  IoArrowBack, 
  IoAddCircleOutline, 
  IoVideocamOutline, 
  IoTrashOutline, 
  IoCalendarOutline, 
  IoTimeOutline, 
  IoChatbubblesOutline, 
  IoHandRightOutline,
  IoAdd,
  IoRemoveCircleOutline,
  IoCheckmarkCircle
} from 'react-icons/io5';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './AsambleasScreen.css';

function GestionAsambleasScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [asambleas, setAsambleas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [asambleaParaIniciar, setAsambleaParaIniciar] = useState(null);
  const [asambleaParaEliminar, setAsambleaParaEliminar] = useState(null);

  // Formulario de Convocatoria
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('ordinaria'); // 'ordinaria' | 'extraordinaria'
  const [fecha, setFecha] = useState('');
  const [hora1, setHora1] = useState('19:00');
  const [hora2, setHora2] = useState('19:30');
  const [descripcion, setDescripcion] = useState('');
  const [ordenDelDia, setOrdenDelDia] = useState([
    'Lista de asistencia y verificación de quórum legal',
    'Lectura y aprobación del orden del día',
    'Presentación y votación de asuntos a tratar',
    'Designación de delegados especiales'
  ]);
  const [nuevoPunto, setNuevoPunto] = useState('');
  const [chatHabilitado, setChatHabilitado] = useState(true);
  const [permitirLevantarMano, setPermitirLevantarMano] = useState(true);

  const condoId = userData?.condominioId;

  useEffect(() => {
    if (!condoId) return;

    const q = query(
      collection(db, 'asambleas'),
      where('condominioId', '==', condoId),
      orderBy('fechaHora1', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setAsambleas(list);
      setLoading(false);
    }, (err) => {
      console.warn("Error leyendo asambleas admin:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [condoId]);

  const handleAddPunto = () => {
    if (!nuevoPunto.trim()) return;
    setOrdenDelDia([...ordenDelDia, nuevoPunto.trim()]);
    setNuevoPunto('');
  };

  const handleRemovePunto = (index) => {
    setOrdenDelDia(ordenDelDia.filter((_, i) => i !== index));
  };

  const handleCrearAsamblea = async (e) => {
    e.preventDefault();
    if (!titulo || !fecha || !hora1) {
      alert('Por favor completa los campos requeridos (Título, Fecha y Hora).');
      return;
    }

    const fechaHora1Iso = `${fecha}T${hora1}:00`;
    const fechaHora2Iso = `${fecha}T${hora2}:00`;

    try {
      // 1. Guardar la Asamblea
      const docRef = await addDoc(collection(db, 'asambleas'), {
        condominioId: condoId,
        titulo,
        tipo,
        fecha,
        hora1,
        hora2,
        fechaHora1: fechaHora1Iso,
        fechaHora2: fechaHora2Iso,
        descripcion,
        ordenDelDia,
        chatHabilitado,
        permitirLevantarMano,
        estatus: 'programada', // 'programada' | 'en_vivo' | 'concluida'
        quorumActual: 0,
        createdAt: serverTimestamp(),
        creadoPor: userData.nombre || 'Administración'
      });

      // 2. Crear un Aviso oficial en el Muro Social y Calendario del Condominio
      await addDoc(collection(db, 'avisos'), {
        condominioId: condoId,
        titulo: `📢 Convocatoria Oficial: ${titulo}`,
        contenido: `Se convoca a todos los condóminos a la ${tipo === 'extraordinaria' ? 'Asamblea Extraordinaria' : 'Asamblea Ordinaria'} que se llevará a cabo el ${fecha}.\n\n• 1ª Convocatoria: ${hora1} hrs.\n• 2ª Convocatoria: ${hora2} hrs.\n\nAcceso a la sala virtual y orden del día disponible en el módulo de Votaciones y Asambleas.`,
        tipo: 'asamblea',
        fecha: serverTimestamp(),
        autor: userData.nombre || 'Administración',
        asambleaId: docRef.id
      });

      // 3. Registrar notificación masiva
      await addDoc(collection(db, 'notificaciones'), {
        condominioId: condoId,
        titulo: `🏛️ Convocatoria a Asamblea General`,
        mensaje: `Se ha programado la asamblea "${titulo}" para el ${fecha} a las ${hora1} hrs.`,
        tipo: 'asamblea',
        url: '/asambleas',
        createdAt: serverTimestamp()
      });

      setActionMsg('✓ Asamblea convocada y notificada a los residentes.');
      setTimeout(() => setActionMsg(''), 4000);
      setShowModal(false);

      // Limpiar Form
      setTitulo('');
      setDescripcion('');
      setFecha('');
    } catch (error) {
      console.error("Error creando asamblea:", error);
      alert("Error al convocar la asamblea: " + error.message);
    }
  };

  const handleStartLive = (asamblea) => {
    setAsambleaParaIniciar(asamblea);
  };

  const confirmarStartLive = async () => {
    if (!asambleaParaIniciar) return;
    const asambleaId = asambleaParaIniciar.id;

    try {
      await updateDoc(doc(db, 'asambleas', asambleaId), {
        estatus: 'en_vivo',
        horaInicioReal: serverTimestamp()
      });

      // Disparar alerta en vivo para campana
      await addDoc(collection(db, 'notificaciones'), {
        condominioId: condoId,
        titulo: '🔴 ¡Estamos en Vivo! Asamblea General',
        mensaje: `La asamblea "${asambleaParaIniciar.titulo || 'General'}" ha comenzado. Toca aquí para ingresar a la sala.`,
        tipo: 'asamblea_en_vivo',
        url: `/asambleas/${asambleaId}/sala`,
        createdAt: serverTimestamp()
      });

      setAsambleaParaIniciar(null);
      navigate(`/asambleas/${asambleaId}/sala`);
    } catch (err) {
      console.error("Error iniciando asamblea:", err);
      alert("Error iniciando la transmisión: " + err.message);
    }
  };

  const handleDeleteAsamblea = (asamblea) => {
    setAsambleaParaEliminar(asamblea);
  };

  const confirmarDeleteAsamblea = async () => {
    if (!asambleaParaEliminar) return;
    try {
      await deleteDoc(doc(db, 'asambleas', asambleaParaEliminar.id));
      setAsambleaParaEliminar(null);
    } catch (err) {
      console.error("Error eliminando asamblea:", err);
      alert("Error eliminando: " + err.message);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/admin/votaciones" />

      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={true} />
        </div>

        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Panel de Asambleas</h1>
                <p>Convoca juntas oficiales, agenda en calendario y transmite en vivo</p>
              </div>
              <IoVideocamOutline className="muro-hero-icon-decor" />
            </div>
          </div>

          <div className="asambleas-container">
            {actionMsg && (
              <div style={{
                background: '#ECFDF5',
                color: '#065F46',
                border: '1px solid #A7F3D0',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '700'
              }}>
                <IoCheckmarkCircle size={20} />
                <span>{actionMsg}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#0F172A', fontWeight: '800' }}>Asambleas del Condominio</h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748B' }}>
                  Gestiona las convocatorias y salas de transmisión en vivo
                </p>
              </div>

              <button
                onClick={() => setShowModal(true)}
                style={{
                  background: '#7E66FC',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 18px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(126, 102, 252, 0.3)'
                }}
              >
                <IoAddCircleOutline size={20} />
                <span>Convocar Nueva Asamblea</span>
              </button>
            </div>

            {/* Listado de Asambleas */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                Cargando asambleas...
              </div>
            ) : asambleas.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '40px 20px',
                textAlign: 'center',
                border: '1px dashed #CBD5E1',
                color: '#64748B'
              }}>
                <IoCalendarOutline size={48} style={{ color: '#94A3B8', marginBottom: '12px' }} />
                <h3 style={{ margin: '0 0 6px', color: '#1E293B', fontWeight: '700' }}>No hay asambleas registradas</h3>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>Haz clic en "Convocar Nueva Asamblea" para crear la primera junta virtual.</p>
              </div>
            ) : (
              asambleas.map(a => (
                <div key={a.id} className="asamblea-card" style={a.estatus === 'en_vivo' ? { border: '2px solid #DC2626' } : {}}>
                  <div className="asamblea-header">
                    <span className={`asamblea-tipo-badge ${a.tipo || 'ordinaria'}`}>
                      {a.tipo === 'extraordinaria' ? 'Extraordinaria' : 'Ordinaria'}
                    </span>

                    <span className={`asamblea-status-badge ${a.estatus}`}>
                      {a.estatus === 'en_vivo' && '🔴 TRANSMITIENDO EN VIVO'}
                      {a.estatus === 'programada' && '📅 Convocatoria Activa'}
                      {a.estatus === 'concluida' && '✓ Concluida'}
                    </span>
                  </div>

                  <div className="asamblea-body">
                    <h3 className="asamblea-title">{a.titulo}</h3>
                    <div className="asamblea-meta-row">
                      <span className="asamblea-meta-item">
                        <IoCalendarOutline size={16} />
                        Fecha: {a.fecha}
                      </span>
                      <span className="asamblea-meta-item">
                        <IoTimeOutline size={16} />
                        1ª Conv: {a.hora1} | 2ª Conv: {a.hora2}
                      </span>
                      <span className="asamblea-meta-item">
                        <IoChatbubblesOutline size={16} />
                        Chat: {a.chatHabilitado ? 'Habilitado' : 'Silenciado'}
                      </span>
                    </div>

                    <div className="asamblea-actions">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {a.estatus === 'programada' && (
                          <button
                            className="btn-asamblea-join"
                            onClick={() => handleStartLive(a)}
                          >
                            <IoVideocamOutline size={18} />
                            <span>Iniciar Transmisión en Vivo</span>
                          </button>
                        )}

                        {a.estatus === 'en_vivo' && (
                          <button
                            className="btn-asamblea-join"
                            onClick={() => navigate(`/asambleas/${a.id}/sala`)}
                          >
                            <IoVideocamOutline size={18} />
                            <span>Continuar en la Sala</span>
                          </button>
                        )}

                        <button
                          style={{
                            background: '#F8FAFC',
                            color: '#334155',
                            border: '1px solid #CBD5E1',
                            borderRadius: '10px',
                            padding: '8px 14px',
                            fontSize: '0.85rem',
                            fontWeight: '700',
                            cursor: 'pointer'
                          }}
                          onClick={() => navigate(`/asambleas/${a.id}/sala`)}
                        >
                          Ver Sala
                        </button>
                      </div>

                      <button
                        onClick={() => handleDeleteAsamblea(a)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.85rem',
                          fontWeight: '700'
                        }}
                      >
                        <IoTrashOutline size={16} />
                        <span>Cancelar</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal de Convocatoria */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '24px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#0F172A', fontWeight: '800' }}>
                  Convocar Asamblea General
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748B' }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCrearAsamblea}>
                {/* Título */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Título de la Asamblea *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Asamblea General Ordinaria Anual 2026"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #CBD5E1', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                {/* Tipo de Asamblea */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Tipo de Asamblea
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="tipo"
                        value="ordinaria"
                        checked={tipo === 'ordinaria'}
                        onChange={(e) => setTipo(e.target.value)}
                      />
                      <span>Ordinaria</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="tipo"
                        value="extraordinaria"
                        checked={tipo === 'extraordinaria'}
                        onChange={(e) => setTipo(e.target.value)}
                      />
                      <span>Extraordinaria</span>
                    </label>
                  </div>
                </div>

                {/* Fecha y Horarios de 1ª y 2ª Convocatoria */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      Fecha *
                    </label>
                    <input
                      type="date"
                      required
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #CBD5E1', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      1ª Convocatoria
                    </label>
                    <input
                      type="time"
                      required
                      value={hora1}
                      onChange={(e) => setHora1(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #CBD5E1', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                      2ª Convocatoria
                    </label>
                    <input
                      type="time"
                      required
                      value={hora2}
                      onChange={(e) => setHora2(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #CBD5E1', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Orden del Día Dinámico */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Puntos del Orden del Día
                  </label>
                  <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '12px', border: '1px solid #E2E8F0', marginBottom: '10px' }}>
                    {ordenDelDia.map((punto, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: index < ordenDelDia.length - 1 ? '1px solid #E2E8F0' : 'none' }}>
                        <span style={{ fontSize: '0.85rem', color: '#334155' }}>
                          <strong>{index + 1}.</strong> {punto}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemovePunto(index)}
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', display: 'flex' }}
                        >
                          <IoRemoveCircleOutline size={18} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Nuevo punto a desahogar..."
                      value={nuevoPunto}
                      onChange={(e) => setNuevoPunto(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPunto(); } }}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: '10px', border: '1px solid #CBD5E1', outline: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={handleAddPunto}
                      style={{ background: '#E2E8F0', border: 'none', borderRadius: '10px', padding: '8px 14px', fontWeight: '700', cursor: 'pointer' }}
                    >
                      + Añadir
                    </button>
                  </div>
                </div>

                {/* Toggles de Chat y Manos */}
                <div style={{ background: '#F1F5F9', padding: '14px', borderRadius: '14px', marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>
                      💬 Habilitar Chat de Participantes
                    </span>
                    <input
                      type="checkbox"
                      checked={chatHabilitado}
                      onChange={(e) => setChatHabilitado(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#334155' }}>
                      ✋ Permitir que los residentes levanten la mano
                    </span>
                    <input
                      type="checkbox"
                      checked={permitirLevantarMano}
                      onChange={(e) => setPermitirLevantarMano(e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{ background: '#F1F5F9', color: '#64748B', border: 'none', borderRadius: '10px', padding: '10px 18px', fontWeight: '700', cursor: 'pointer' }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{ background: '#7E66FC', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 22px', fontWeight: '800', cursor: 'pointer' }}
                  >
                    Publicar y Convocar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL PREMIUM: Confirmar Inicio de Transmisión en Vivo */}
        {asambleaParaIniciar && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: '#FFFFFF',
              borderRadius: '24px',
              maxWidth: '460px',
              width: '100%',
              padding: '28px 24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              textAlign: 'center',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              animation: 'slideUp 0.25s ease-out'
            }}>
              {/* Icono animado */}
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#FEE2E2',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '28px',
                boxShadow: '0 0 0 8px rgba(239, 68, 68, 0.15)'
              }}>
                <IoVideocamOutline size={32} />
              </div>

              <span style={{
                background: '#FEE2E2',
                color: '#DC2626',
                fontSize: '0.75rem',
                fontWeight: '800',
                padding: '4px 12px',
                borderRadius: '20px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                🔴 Iniciar Transmisión
              </span>

              <h2 style={{ margin: '14px 0 8px', fontSize: '1.25rem', fontWeight: '800', color: '#0F172A' }}>
                {asambleaParaIniciar.titulo || 'Asamblea General'}
              </h2>

              <p style={{ margin: '0 0 24px', fontSize: '0.9rem', color: '#64748B', lineHeight: '1.5' }}>
                Estás a punto de abrir la sala virtual en vivo. Se enviará una <strong>notificación Push</strong> de alta prioridad a los teléfonos de todos los condóminos para que se unan de inmediato.
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setAsambleaParaIniciar(null)}
                  style={{
                    flex: 1,
                    background: '#F1F5F9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmarStartLive}
                  style={{
                    flex: 1.4,
                    background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  Iniciar en Vivo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL PREMIUM: Confirmar Cancelación / Eliminación */}
        {asambleaParaEliminar && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: '#FFFFFF',
              borderRadius: '24px',
              maxWidth: '420px',
              width: '100%',
              padding: '28px 24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              textAlign: 'center',
              animation: 'slideUp 0.25s ease-out'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#FEE2E2',
                color: '#DC2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <IoTrashOutline size={26} />
              </div>

              <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem', fontWeight: '800', color: '#0F172A' }}>
                ¿Cancelar esta Asamblea?
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: '#64748B' }}>
                Esta acción eliminará la convocatoria de forma permanente para todos los residentes.
              </p>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setAsambleaParaEliminar(null)}
                  style={{
                    flex: 1,
                    background: '#F1F5F9',
                    color: '#475569',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  Regresar
                </button>
                <button
                  type="button"
                  onClick={confirmarDeleteAsamblea}
                  style={{
                    flex: 1,
                    background: '#DC2626',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px',
                    fontWeight: '800',
                    cursor: 'pointer'
                  }}
                >
                  Sí, Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="ks-bottom-bar">
          <KondoBottomNav activeRoute="/admin/votaciones" />
        </div>
      </main>
    </div>
  );
}

export default GestionAsambleasScreen;
